/**
 * The calendar's URL state.
 *
 * Same rule as Tasks and Contacts: what a person is looking at IS the address. "Oktober, den
 * fjortende" has to be a link a colleague can be sent, and a reload has to land on it rather
 * than on today. So the month and the open day write to the address bar instead of to a
 * `useState`, and this module — not a component — decides what a valid `/calendar` URL is.
 *
 * TWO PARAMETERS, AND THE MONTH IS NOT DERIVED FROM THE DAY. `?month=2026-11&date=2026-12-01`
 * is a real state: the grid shows November and the day panel is open on a trailing cell that
 * belongs to December. Deriving the month from the day would make clicking that trailing cell
 * jump the grid to December — which is the reader losing their place at the one moment they
 * asked for something small.
 *
 * ABSENT MEANS DEFAULT, so `?month=` is never written for the month containing today: the plain
 * `/calendar` the menu links to and the address a person types are the same location, and the
 * router does not treat them as two.
 *
 * WHAT THIS MODULE DOES NOT DECIDE. Whether `date` is one of the 42 days the grid can draw is a
 * question about the reader's LOCALE — a Monday-first grid and a Sunday-first grid borrow
 * different days from the neighbouring months — and `validateSearch` runs at the router's edge
 * where there is no locale. The page asks `isInWindow` once it has built the model; a `date`
 * outside it renders no panel rather than a panel about a day nobody can see.
 */
import { CALENDAR_KINDS, ORGANISATION_ONLY, type CalendarKind } from '@tvitvi/shared';

import { monthKey, parseDateValue, parseMonthKey } from './month-grid';

/**
 * KOI DEL 3 GA SIDEN FIRE VISNINGER OG SEKS FILTRE, og alle sammen er adressen.
 *
 * Det er den samme regelen månedene allerede fulgte, brukt på resten: «uke 28, bare skift, bare
 * produksjonsavdelingen» må være en lenke en kollega kan få. Alternativet — `useState` i
 * verktøylinja — gjør hver eneste kontroll til noe som forsvinner ved en omlasting, og et filter
 * som stilltiende nullstiller seg er verre enn intet filter: man tror man ser alt.
 *
 * `view` ER IKKE UTLEDET AV NOE. Måned, uke, dag og agenda er fire lesninger av det samme vinduet,
 * og hvilken man står i sier ingenting om hvilken dag man ser på. Derfor to parametre og ikke én.
 */
export const CALENDAR_VIEWS = ['month', 'week', 'day', 'agenda'] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

export interface CalendarSearch {
  /** `YYYY-MM`. Absent means the month containing today. */
  month?: string;
  /** `YYYY-MM-DD`. Absent means no day is open. */
  date?: string;
  /** Måned er standarden og skrives derfor aldri: `/calendar` og `?view=month` er ett sted. */
  view?: CalendarView;
  /** Filterchipsene. Utelatt betyr alle — det er ikke det samme som en tom liste. */
  kinds?: readonly CalendarKind[];
  departmentId?: string;
  personId?: string;
  /** «Min kalender». Skrives bare når den står på. */
  mine?: boolean;
  q?: string;
  /**
   * Hvilke arrangementer man vil se (QA13 runde 2, #39). Utelatt betyr alle.
   *
   * `ORGANISATION_ONLY` er ett av valgene — et element uten årgang er like mye en del av
   * utvalget som en festival er. Se kontrakten for hvorfor det er et ord og ikke en null.
   */
  editions?: readonly string[];
  /**
   * En hendelse som skal åpnes i panelet — varselet «inviterte deg til …» lenker hit (QA5-B).
   * Leses én gang og fjernes fra adressen; den er en dør inn, ikke en del av visningen.
   */
  event?: string;
}

/**
 * The router's edge. Anything that is not a real month or a real day is dropped rather than
 * corrected: `?month=2026-13` is not a typo for December, and guessing at one would send a
 * reader somewhere they did not ask for.
 */
/** En uuid slik ruteren ser den — små bokstaver, med bindestreker. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateCalendarSearch(raw: Record<string, unknown>): CalendarSearch {
  const search: CalendarSearch = {};

  const month = raw['month'];
  if (typeof month === 'string' && parseMonthKey(month) !== null) search.month = month;

  const date = raw['date'];
  if (typeof date === 'string' && parseDateValue(date) !== null) search.date = date;

  const view = raw['view'];
  if (typeof view === 'string' && (CALENDAR_VIEWS as readonly string[]).includes(view)) {
    search.view = view as CalendarView;
  }

  /*
   * TYPENE KOMMER SOM EN KOMMALISTE, og en liste der ALT er ukjent behandles som fraværende.
   *
   * `?kinds=fisk` skal ikke gi en tom kalender — en tom kalender ser ut som «ingenting skjer»,
   * ikke som «du skrev noe rart». En liste der NOE er gyldig beholder det gyldige: `?kinds=task,fisk`
   * er en lenke der oppgavefilteret virker, og det er den lesningen som gjør minst skade.
   */
  const kinds = parseList(raw['kinds']).filter((one): one is CalendarKind =>
    (CALENDAR_KINDS as readonly string[]).includes(one),
  );
  if (kinds.length > 0) search.kinds = kinds;

  /*
   * ARRANGEMENTUTVALGET (QA13 runde 2, #39), etter den samme regelen som `kinds` over: det som er
   * gyldig beholdes, og resten faller bort framfor å tømme kalenderen.
   *
   * Formen godtas uten å slås opp mot husets årganger: en id som ikke finnes filtrerer bort alt
   * den ikke matcher, som er den samme oppførselen en slettet årgang ville gitt. Å validere den
   * her ville krevd et nettverkskall inne i ruterens kant.
   */
  const editions = parseList(raw['editions']).filter(
    (one) => one === ORGANISATION_ONLY || UUID.test(one),
  );
  if (editions.length > 0) search.editions = editions;

  const department = raw['departmentId'];
  if (typeof department === 'string' && department.length > 0) search.departmentId = department;

  const person = raw['personId'];
  if (typeof person === 'string' && person.length > 0) search.personId = person;

  /* `?mine` uten verdi er også på — det er slik en flaggparameter skrives for hånd. */
  const mine = raw['mine'];
  if (mine === true || mine === 'true' || mine === '') search.mine = true;

  const q = raw['q'];
  if (typeof q === 'string' && q.trim().length > 0) search.q = q.trim().slice(0, 200);

  const event = raw['event'];
  if (typeof event === 'string' && UUID.test(event)) search.event = event;

  return search;
}

/** En kommaliste, enten den kom som en streng eller som et array (routeren gir begge). */
function parseList(raw: unknown): readonly string[] {
  if (Array.isArray(raw)) return raw.filter((one): one is string => typeof one === 'string');
  if (typeof raw === 'string') return raw.split(',').filter((one) => one.length > 0);
  return [];
}

/**
 * Which month the grid draws.
 *
 * `month` if it names one, else the month of the open day — so a bare `/calendar?date=2026-12-24`
 * is a link that works — else the month containing today.
 */
export function monthOf(search: CalendarSearch, today: Date): Date {
  const fromMonth = search.month === undefined ? null : parseMonthKey(search.month);
  if (fromMonth !== null) return fromMonth;

  const fromDate = search.date === undefined ? null : parseDateValue(search.date);
  if (fromDate !== null) return new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);

  return new Date(today.getFullYear(), today.getMonth(), 1);
}

/**
 * The address for a month with no day open.
 *
 * Moving between months drops `date` on purpose. The panel describes a day in the month you are
 * reading; carrying it into the next month would leave a heading about the fourteenth of October
 * beside a grid of November, and the reader would have to work out which of the two lied.
 */
export function searchForMonth(month: Date, today: Date, from?: CalendarSearch): CalendarSearch {
  const key = monthKey(month);
  const base = key === monthKey(today) ? {} : { month: key };
  return { ...keepFilters(from), ...base };
}

/** The address for one day, inside the month the grid is currently drawing. */
export function searchForDay(
  month: Date,
  date: string,
  today: Date,
  from?: CalendarSearch,
): CalendarSearch {
  return { ...searchForMonth(month, today, from), date };
}

/**
 * FILTRENE OVERLEVER EN NAVIGERING, DAGEN GJØR DET IKKE.
 *
 * Det er den samme grensen `searchForMonth` allerede trakk for `date`, utvidet til resten: den som
 * har skrudd på «bare skift» og blar til neste uke mener fortsatt «bare skift» — men den som blar
 * videre mener ikke lenger den fjortende. Et filter er en LESEMÅTE og følger med; en dag er et
 * STED og gjør det ikke.
 */
export function keepFilters(search: CalendarSearch | undefined): CalendarSearch {
  if (search === undefined) return {};
  const kept: CalendarSearch = {};
  if (search.view !== undefined) kept.view = search.view;
  if (search.kinds !== undefined) kept.kinds = search.kinds;
  if (search.departmentId !== undefined) kept.departmentId = search.departmentId;
  if (search.personId !== undefined) kept.personId = search.personId;
  if (search.mine === true) kept.mine = true;
  if (search.q !== undefined) kept.q = search.q;
  if (search.editions !== undefined) kept.editions = search.editions;
  return kept;
}

/**
 * DET KALENDEREN HUSKER MELLOM ØKTER (239.45, Atle 04.10: «Husk visningen, men ikke søket»).
 *
 * Visningen, typene, avdelingen, personen, «mine» og årgangene huskes per bruker på serveren. Søket
 * gjør ikke det: neste dag åpnet kalenderen filtrert på et gammelt søk, og det så ut som om
 * hendelsene var borte. Søket står fortsatt i adressen og følger med når man blar.
 */
export function rememberedView(search: CalendarSearch | undefined): CalendarSearch {
  const kept = keepFilters(search);
  delete kept.q;
  return kept;
}
